import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Divider,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Button,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { WidgetContainer } from '../../widgets.styles';
import { getInitials } from '../../../../../../utils/get-initials.util';
import { useAuth } from '../../../../../../context/auth.context';
import { useSnackbar } from '../../../../../../context/snackbar.context';
import { followService, notificationsService } from '../../../../../../services/service-proxy';

const { getSuggestedUsers, followUser } = followService;
const { createNotification } = notificationsService;

const WhoToFollow = () => {
  const { user, profile } = useAuth();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingState, setFollowingState] = useState({});

  // Load suggested users
  useEffect(() => {
    let cancelled = false;

    const loadSuggestions = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const users = await getSuggestedUsers(user.uid, 5);
        if (!cancelled) {
          setSuggestions(users);
          const initialState = {};
          users.forEach((u) => {
            initialState[u.id] = 'idle';
          });
          setFollowingState(initialState);
        }
      } catch (err) {
        // Silently fail
      }
      if (!cancelled) {
        setLoading(false);
      }
    };

    loadSuggestions();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Handle inline follow
  const handleFollow = useCallback(async (targetUser) => {
    if (!user || !profile) return;

    setFollowingState((prev) => ({ ...prev, [targetUser.id]: 'loading' }));

    try {
      await followUser(user.uid, targetUser.id);
      setFollowingState((prev) => ({ ...prev, [targetUser.id]: 'followed' }));
      showSnackbar(`Following ${targetUser.displayName}!`, 'success');

      createNotification({
        recipientId: targetUser.id,
        senderId: user.uid,
        senderName: profile.displayName || '',
        senderUsername: profile.username || '',
        senderAvatarUrl: profile.avatarUrl || null,
        type: 'follow',
        postId: null,
        message: `${profile.displayName || 'Someone'} started following you.`,
      });
    } catch (err) {
      setFollowingState((prev) => ({ ...prev, [targetUser.id]: 'idle' }));
      showSnackbar('Failed to follow user.', 'error');
    }
  }, [user, profile, showSnackbar]);

  const handleUserClick = useCallback((username) => {
    navigate(`/profile/${username}`);
  }, [navigate]);

  const handleShowMore = useCallback(() => {
    navigate('/explore');
  }, [navigate]);

  if (loading) {
    return (
      <WidgetContainer>
        <Typography sx={{ p: 2.5 }} variant="h6" color="text.primary">
          Who To Follow
        </Typography>
        <Divider />
        <Box sx={{ p: 2 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="40%" height={16} />
              </Box>
              <Skeleton variant="rounded" width={70} height={30} />
            </Box>
          ))}
        </Box>
      </WidgetContainer>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <WidgetContainer>
      <Typography sx={{ p: 2.5 }} variant="h6" color="text.primary">
        Who To Follow
      </Typography>
      <Divider />
      <List sx={{ width: '100%' }} aria-label="Suggested users to follow">
        {suggestions.map((suggestedUser, index) => {
          const state = followingState[suggestedUser.id] || 'idle';

          return (
            <React.Fragment key={suggestedUser.id}>
              {index > 0 && <Divider />}
              <ListItem sx={{ px: 2.5, py: 1.5 }}>
                <ListItemAvatar
                  sx={{ cursor: 'pointer', minWidth: 48 }}
                  onClick={() => handleUserClick(suggestedUser.username)}
                >
                  <Avatar
                    src={suggestedUser.avatarUrl || undefined}
                    alt={`${suggestedUser.displayName}'s avatar`}
                  >
                    {getInitials(suggestedUser.displayName || '')}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={suggestedUser.displayName}
                  secondary={`@${suggestedUser.username}`}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleUserClick(suggestedUser.username)}
                />
                {state === 'followed' ? (
                  <Button variant="outlined" size="small" disabled>
                    Following
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleFollow(suggestedUser)}
                    disabled={state === 'loading'}
                    aria-label={`Follow ${suggestedUser.displayName}`}
                  >
                    {state === 'loading' ? (
                      <CircularProgress size={16} />
                    ) : (
                      'Follow'
                    )}
                  </Button>
                )}
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
        <Button size="small" onClick={handleShowMore}>
          Show more
        </Button>
      </Box>
    </WidgetContainer>
  );
};

export default WhoToFollow;
